import { observable, computed, ObservableMap, toJS, makeObservable } from 'mobx'
import { TILE_CONTENT } from '@mixtiles/web-backend-shared'
import { isExpiredPhoto } from 'components/PhotoBank/photoUtils'
import { IS_COLLAGE_CREATION_AVAILABLE } from 'services/Collage/collage.consts'
import { BaseTileStore } from './BaseTileStore'
import { CollagePhotoSlot } from './CollagePhotoSlot'
import { type CollageLayout, type CollageText } from '../TilesStore.types'
import type { PhotoBankStore } from '../../photoBankStore/PhotoBankStore'
import type { CollageTileData, CollagePhotoSlotData } from './tileSchemas'
import { type Tile } from 'components/TilesProvider/TilesProvider.types'
import { type TilePersistenceDisposition } from './BaseTileStore'
import { type TilePatch, type TilePatchMode } from '../TilePatch'

const SAVE_COLLAGE_KEYS = ['collage']
const COLLAGE_PATCHABLE_KEYS_WHITELIST = new Set([
  'isCreating',
  'processingLayout',
])

export class CollageTileStore extends BaseTileStore {
  @observable.shallow accessor photos: ObservableMap<string, CollagePhotoSlot>

  @observable.deep accessor layout: CollageLayout | undefined

  @observable.deep accessor collageText: CollageText

  @observable accessor isCreating = false

  @observable accessor processingLayout: unknown | undefined

  constructor(data: CollageTileData, photoBankStore: PhotoBankStore) {
    super(data, photoBankStore)

    this.layout = data.collage?.layout ?? data.layout
    this.collageText =
      data.collage?.collageText ?? (data.collageText as CollageText)
    this.isCreating = data.isCreating ?? false

    const rawPhotos = data.collage?.photos ?? data.photos ?? {}

    this.photos = new ObservableMap<string, CollagePhotoSlot>()
    for (const [positionId, photoData] of Object.entries(rawPhotos)) {
      this.photos.set(
        positionId,
        new CollagePhotoSlot(photoData, this.photoBankStore)
      )
    }
    makeObservable(this)
  }

  @computed
  get collage() {
    if (!this.layout) return undefined

    const photosObj: Record<string, CollagePhotoSlot> = {}
    this.photos.forEach((slot, key) => {
      photosObj[key] = slot
    })
    return {
      photos: photosObj,
      layout: this.layout,
      collageText: this.collageText,
    }
  }

  get tileContent(): TILE_CONTENT {
    return TILE_CONTENT.COLLAGE
  }

  override getPhotoUids(): string[] {
    return [...this.photos.values()]
      .map((slot) => slot.uid ?? '')
      .filter(Boolean)
  }

  override hasValidRequiredPhotos(): boolean {
    // Saved collages are dropped on restore too, so no collage reaches checkout while creation is off
    if (!IS_COLLAGE_CREATION_AVAILABLE) return false
    return [...this.photos.values()]
      .filter((slot) => !!slot.uid)
      .every((slot) => !isExpiredPhoto(slot.photo))
  }

  override getPersistenceState(): TilePersistenceDisposition {
    return this.photos.size === 0 ? 'skip' : 'ready'
  }

  override toTileData(): CollageTileData {
    return {
      ...this.baseTileData(),
      tileContent: TILE_CONTENT.COLLAGE,
      layout: toJS(this.layout),
      collageText: toJS(this.collageText),
      isCreating: this.isCreating,
      photos: Object.fromEntries(
        [...this.photos.entries()].map(([positionId, slot]) => [
          positionId,
          {
            uid: slot.uid,
            position: toJS(slot.position),
            cropParams: toJS(slot.cropParams),
            cropperData: toJS(slot.cropperData),
          },
        ])
      ),
    }
  }

  override get persistenceKeys(): string[] {
    return super.persistenceKeys.concat(SAVE_COLLAGE_KEYS)
  }

  protected override getPatchableKeysWhitelist(
    mode: TilePatchMode
  ): Set<string> {
    return new Set([
      ...super.getPatchableKeysWhitelist(mode),
      ...COLLAGE_PATCHABLE_KEYS_WHITELIST,
    ])
  }

  protected override applyPatchedField(
    key: string,
    value: unknown,
    mode: TilePatchMode
  ): boolean {
    if (key === 'collage' && value) {
      this.applyCollagePatch(value as NonNullable<TilePatch['collage']>)
      return true
    }

    return super.applyPatchedField(key, value, mode)
  }

  private applyCollagePatch(collage: NonNullable<TilePatch['collage']>) {
    if (collage.layout !== undefined) {
      this.layout = collage.layout as typeof this.layout
    }

    if (collage.collageText !== undefined) {
      this.collageText = collage.collageText as typeof this.collageText
    }

    if (collage.photos !== undefined) {
      this.photos.clear()
      for (const [positionId, slotData] of Object.entries(
        collage.photos as Record<string, CollagePhotoSlotData>
      )) {
        this.photos.set(
          positionId,
          new CollagePhotoSlot(slotData, this.photoBankStore)
        )
      }
    }
  }

  protected override _buildProviderSnapshot(): Tile {
    const result = super._buildProviderSnapshot()
    result.processingLayout = toJS(this.processingLayout)
    result.isCreating = this.isCreating
    if (this.isCreating) {
      // We have conditions that rely on the presence of the collage field instead of isCreating, thus we need to remove it when creating.
      delete result.collage
      return result
    }
    const photos: Record<string, unknown> = {}
    Array.from(this.photos.entries()).forEach(([key, slot]) => {
      const slotData = slot.toJSON()
      if (slot.photo) Object.assign(slotData, toJS(slot.photo))
      photos[key] = slotData
    })
    result.collage = {
      layout: toJS(this.layout),
      photos,
      collageText: toJS(this.collageText),
    }
    return result
  }

  override toJSON(): Record<string, unknown> {
    const base = super.toJSON()
    const photosObj: Record<string, unknown> = {}
    this.photos.forEach((slot, key) => {
      photosObj[key] = slot.toJSON()
    })
    base.collage = {
      layout: toJS(this.layout),
      photos: photosObj,
      collageText: toJS(this.collageText),
    }
    return base
  }
}
